import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Containers from './pages/Containers'
import ContainerDetail from './pages/ContainerDetail'
import VMs from './pages/VMs'
import VMDetail from './pages/VMDetail'
import Storage from './pages/Storage'
import ComingSoon from './pages/ComingSoon'
import Login from './pages/Login'
import Signup from './pages/Signup'
import { WebSocketProvider } from './hooks/useWebSocket'
import { useAuth } from './hooks/useAuth'

// RequireAuth gates the app shell: unauthenticated users are bounced to /login
// (remembering where they were headed). While we validate an existing token we
// show a spinner rather than flashing the login page.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { token, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-voltainer-500" />
      </div>
    )
  }
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return <>{children}</>
}

function AppShell() {
  return (
    <WebSocketProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/vms" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/containers" element={<Containers />} />
          <Route path="/containers/:id" element={<ContainerDetail />} />
          <Route path="/vms" element={<VMs />} />
          <Route path="/vms/:id" element={<VMDetail />} />
          <Route path="/storage" element={<Storage />} />
          <Route
            path="/network"
            element={<ComingSoon title="Network" description="Bridge and network configuration" />}
          />
          <Route
            path="/users"
            element={<ComingSoon title="Users" description="Access control and authentication" />}
          />
          <Route path="*" element={<Navigate to="/vms" replace />} />
        </Routes>
      </Layout>
    </WebSocketProvider>
  )
}

function App() {
  return (
    <Routes>
      {/* Public auth routes render standalone (no sidebar). */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      {/* Everything else requires a session. */}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      />
    </Routes>
  )
}

export default App
